/**
 * AWS Mapping Hook
 *
 * This custom hook provides functionality for fetching and managing AWS (Automatic Weather Station) mappings ,Used in the Flag and offset page/forms.
 * It uses React Query for data fetching, caching, and pagination.
 *
 * Features:
 * - Infinite scrolling support
 * - Automatic caching
 * - Manual refresh capability
 * - Pagination handling
 */

import { useInfiniteQuery, useQueryClient } from "react-query";
import { getFormData } from "./api";

/**
 * Fetches AWS mappings with pagination
 * @param {Object} params - Query parameters
 * @param {number} [params.pageParam=0] - The page number to fetch
 * @returns {Promise<Object>} The response containing AWS mappings and pagination info
 */
const fetchAwsMapping = async ({ pageParam = 0 }) => {
  const response = await getFormData("aws-mappings", {
    page: pageParam,
    limit: 5,
  });
  return {
    aws_mappings: response.aws_mappings,
    nextPage: response.has_more ? pageParam + 1 : undefined,
    totalCount: response.total_count,
  };
};

/**
 * Custom hook for managing AWS mappings
 * @returns {Object} Query results and utility functions
 * @property {Object} data - The query data
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 * @property {Function} refetchRows - Function to manually refresh the data
 */
export const useAwsMapping = () => {
  const queryClient = useQueryClient();

  const query = useInfiniteQuery("aws-mappings", fetchAwsMapping, {
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Cache kept for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch when component mounts
  });

  /**
   * Manually refreshes the AWS mappings data
   * @returns {Promise} A promise that resolves when the data is refreshed
   */
  const refetchRows = async () => {
    await queryClient.resetQueries("aws-mappings");
    return query.refetch();
  };

  return { ...query, refetchRows };
};
